import echarts from '../echartsConfig';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
	DatasetComponentOption,
	ToolboxComponentOption,
	TitleComponentOption,
	TooltipComponentOption,
	LegendComponentOption,
	GridComponentOption,
	DataZoomComponentOption
} from 'echarts/components';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import { useTheme } from '@/components/theme/useTheme';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { fillGapsWithNull } from '@/utils/chartUtils';

export interface ChartDataPoint {
	x: string | number;
	y: number | null;

	[key: string]: string | number | null;
}

export interface ChartSeries {
	name: string;
	data: ChartDataPoint[];
	type?: 'line' | 'scatter';
	color?: string;
	showSymbol?: boolean;
	symbolSize?: number;
}

interface Props {
	series: ChartSeries[];
	title?: string;
	fillGaps?: boolean;
	initiallyVisibleSeries?: string[];
	resetKey?: string;
}

type DataZoomRange = {
	start?: number;
	end?: number;
	startValue?: string | number;
	endValue?: string | number;
};

type DataZoomEventPayload = DataZoomRange & {
	batch?: DataZoomRange[];
};

function isSeriesVisibleByDefault(seriesName: string, initiallyVisibleSeries?: string[]) {
	return initiallyVisibleSeries ? initiallyVisibleSeries.includes(seriesName) : true;
}

function buildVisibleSeriesByName(series: ChartSeries[], initiallyVisibleSeries?: string[]) {
	return series.reduce<Record<string, boolean>>((acc, chartSeries) => {
		acc[chartSeries.name] = isSeriesVisibleByDefault(chartSeries.name, initiallyVisibleSeries);
		return acc;
	}, {});
}

function hasDataZoomRange(payload?: DataZoomRange) {
	return (
		payload?.start !== undefined ||
		payload?.end !== undefined ||
		payload?.startValue !== undefined ||
		payload?.endValue !== undefined
	);
}

function getDataZoomRangeFromEvent(event: DataZoomEventPayload): DataZoomRange | null {
	const source = event.batch?.find(hasDataZoomRange) ?? event;
	const range: DataZoomRange = {};

	if (source.start !== undefined) {
		range.start = source.start;
	}

	if (source.end !== undefined) {
		range.end = source.end;
	}

	if (source.startValue !== undefined) {
		range.startValue = source.startValue;
	}

	if (source.endValue !== undefined) {
		range.endValue = source.endValue;
	}

	return hasDataZoomRange(range) ? range : null;
}

function getDefaultDataZoomRange(): DataZoomRange {
	return { start: 0, end: 100 };
}

function getDataPointTimestamp(point: ChartDataPoint): number | null {
	if (typeof point.x === 'number') {
		return Number.isFinite(point.x) ? point.x : null;
	}

	const timestamp = Date.parse(point.x);

	return Number.isNaN(timestamp) ? null : timestamp;
}

function getDataWindowKey(series: ChartSeries[]): string {
	let minTimestamp: number | null = null;
	let maxTimestamp: number | null = null;

	for (const chartSeries of series) {
		for (const point of chartSeries.data) {
			const timestamp = getDataPointTimestamp(point);

			if (timestamp === null) {
				continue;
			}

			minTimestamp = minTimestamp === null ? timestamp : Math.min(minTimestamp, timestamp);
			maxTimestamp = maxTimestamp === null ? timestamp : Math.max(maxTimestamp, timestamp);
		}
	}

	return minTimestamp === null || maxTimestamp === null ? '' : `${minTimestamp}:${maxTimestamp}`;
}

export default function LineChart({ series, title, initiallyVisibleSeries, fillGaps = false, resetKey }: Props) {
	const { theme } = useTheme();

	const processedSeries = fillGaps
		? series.map(s => ({ ...s, data: fillGapsWithNull(s.data) }))
		: series;

	const nonEmptyDataSeries = processedSeries.filter(ds => ds.data.length > 0);
	const nonEmptyDataSeriesRef = useRef<ChartSeries[]>([]);
	const dataZoomRangeRef = useRef<DataZoomRange>(getDefaultDataZoomRange());
	const dataZoomResetKey = resetKey ?? getDataWindowKey(nonEmptyDataSeries);
	const dataZoomResetKeyRef = useRef(dataZoomResetKey);

	if (dataZoomResetKeyRef.current !== dataZoomResetKey) {
		dataZoomRangeRef.current = getDefaultDataZoomRange();
		dataZoomResetKeyRef.current = dataZoomResetKey;
	}

	nonEmptyDataSeriesRef.current = nonEmptyDataSeries;
	const [visibleSeriesByName, setVisibleSeriesByName] = useState<Record<string, boolean>>(() =>
		buildVisibleSeriesByName(nonEmptyDataSeries, initiallyVisibleSeries)
	);

	const seriesNamesKey = nonEmptyDataSeries.map(chartSeries => chartSeries.name).join('\u0000');
	const initiallyVisibleSeriesKey = initiallyVisibleSeries?.join('\u0000') ?? '';

	useEffect(() => {
		const seriesNames = seriesNamesKey ? seriesNamesKey.split('\u0000') : [];

		setVisibleSeriesByName(prev => {
			const next: Record<string, boolean> = {};
			let changed = Object.keys(prev).length !== seriesNames.length;

			for (const seriesName of seriesNames) {
				const existingVisibility = prev[seriesName];
				const nextVisibility =
					existingVisibility ?? isSeriesVisibleByDefault(seriesName, initiallyVisibleSeries);

				next[seriesName] = nextVisibility;

				if (!changed && existingVisibility !== nextVisibility) {
					changed = true;
				}
			}

			if (!changed) {
				for (const seriesName of Object.keys(prev)) {
					if (!(seriesName in next)) {
						changed = true;
						break;
					}
				}
			}

			return changed ? next : prev;
		});
	}, [initiallyVisibleSeries, initiallyVisibleSeriesKey, seriesNamesKey]);

	const handleLegendSelectChanged = useCallback((event: { selected: Record<string, boolean> }) => {
		setVisibleSeriesByName(prev => {
			const next = { ...prev };
			let changed = false;

			for (const chartSeries of nonEmptyDataSeriesRef.current) {
				const nextVisibility = event.selected[chartSeries.name];

				if (typeof nextVisibility === 'boolean' && next[chartSeries.name] !== nextVisibility) {
					next[chartSeries.name] = nextVisibility;
					changed = true;
				}
			}

			return changed ? next : prev;
		});
	}, []);

	const handleDataZoom = useCallback((event: DataZoomEventPayload) => {
		const range = getDataZoomRangeFromEvent(event);

		if (range) {
			dataZoomRangeRef.current = range;
		}
	}, []);

	const onEvents = useMemo(
		() => ({
			legendselectchanged: handleLegendSelectChanged,
			datazoom: handleDataZoom
		}),
		[handleDataZoom, handleLegendSelectChanged]
	);

	if (nonEmptyDataSeries.length === 0) {
		return (
			<Alert>
				<AlertDescription>No data available for plotting.</AlertDescription>
			</Alert>
		);
	}

	const renderedSeries = nonEmptyDataSeries.map(chartSeries => ({
		...chartSeries,
		data: visibleSeriesByName[chartSeries.name] ? chartSeries.data : []
	}));

	const datasets: DatasetComponentOption[] = renderedSeries.map(chartSeries => ({
		source: chartSeries.data
	}));

	const legendSelected = nonEmptyDataSeries.reduce<Record<string, boolean>>((acc, chartSeries) => {
		acc[chartSeries.name] = visibleSeriesByName[chartSeries.name] ?? true;
		return acc;
	}, {});

	const option: echarts.ComposeOption<
		| TitleComponentOption
		| TooltipComponentOption
		| LegendComponentOption
		| GridComponentOption
		| ToolboxComponentOption
		| DatasetComponentOption
		| DataZoomComponentOption
	> = {
		useUTC: true,
		title: { text: title },
		tooltip: { trigger: 'axis' },
		legend: {
			data: nonEmptyDataSeries.map(s => s.name),
			selected: legendSelected
		},
		toolbox: { feature: { saveAsImage: {} } },
		dataset: datasets,
		xAxis: { type: 'time' },
		yAxis: { type: 'value' },
		series: renderedSeries.map((chartSeries, idx) => ({
			datasetIndex: idx,
			showSymbol: chartSeries.showSymbol ?? false,
			name: chartSeries.name,
			type: chartSeries.type ?? 'line',
			encode: { x: 'x', y: 'y' },
			connectNulls: !fillGaps,
			sampling: 'none',
			color: chartSeries.color,
			symbolSize: chartSeries.symbolSize,
			lineStyle: chartSeries.type === 'scatter' ? { width: 0 } : undefined
		})),
		dataZoom: [
			{
				type: 'inside',
				...dataZoomRangeRef.current,
				filterMode: 'none',
				zoomOnMouseWheel: false,
				moveOnMouseWheel: false
			},
			{
				type: 'slider',
				...dataZoomRangeRef.current,
				bottom: 10,
				height: 20,
				filterMode: 'none'
			}
		]
	};

	return (
		<div className="h-full">
			<ReactEChartsCore
				echarts={echarts}
				option={option}
				notMerge={true}
				lazyUpdate={false}
				onEvents={onEvents}
				theme={theme}
				style={{ width: '100%', height: '600px' }}
			/>
		</div>
	);
}
